import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardDrive, Mail, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function GoogleActions({ docType, docId, docNumber, customerEmail }) {
  const [exporting, setExporting] = useState(false);
  const [gmailOpen, setGmailOpen] = useState(false);
  const [gmailForm, setGmailForm] = useState({ to_email: customerEmail || "", subject: "", body: "" });
  const [sendingDraft, setSendingDraft] = useState(false);
  const [driveLink, setDriveLink] = useState(null);

  const handleExportDrive = async () => {
    setExporting(true);
    try {
      const { data } = await api.post("/google/drive/export", { doc_type: docType, doc_id: docId });
      setDriveLink(data.drive_link);
      toast.success("PDF esportato su Google Drive!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Errore";
      toast.error(formatApiError(msg));
    } finally {
      setExporting(false);
    }
  };

  const handleCreateDraft = async () => {
    if (!gmailForm.to_email) { toast.error("Inserisci l'email del destinatario"); return; }
    setSendingDraft(true);
    try {
      await api.post("/google/gmail/draft", {
        doc_type: docType,
        doc_id: docId,
        to_email: gmailForm.to_email,
        subject: gmailForm.subject || undefined,
        body: gmailForm.body || undefined,
      });
      toast.success("Bozza Gmail creata! Apri Gmail per inviarla.");
      setGmailOpen(false);
    } catch (err) {
      const msg = err.response?.data?.detail || "Errore";
      toast.error(formatApiError(msg));
    } finally {
      setSendingDraft(false);
    }
  };

  const docLabel = { quote: "Preventivo", inventory: "Inventario", proforma: "Proforma" }[docType] || "Documento";

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button variant="outline" size="sm" onClick={handleExportDrive} disabled={exporting}
          className="rounded-sm text-xs gap-1.5" data-testid="export-drive-btn">
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <HardDrive className="w-3.5 h-3.5" />}
          Esporta su Drive
        </Button>
        {driveLink && (
          <a href={driveLink} target="_blank" rel="noopener noreferrer"
            className="text-xs text-[#002FA7] hover:underline flex items-center gap-1" data-testid="drive-link">
            <ExternalLink className="w-3 h-3" /> Apri in Drive
          </a>
        )}
        <Button variant="outline" size="sm" onClick={() => { setGmailForm({ ...gmailForm, to_email: customerEmail || "" }); setGmailOpen(true); }}
          className="rounded-sm text-xs gap-1.5" data-testid="prepare-gmail-btn">
          <Mail className="w-3.5 h-3.5" /> Prepara email in Gmail
        </Button>
      </div>

      <Dialog open={gmailOpen} onOpenChange={setGmailOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Prepara bozza Gmail</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500">Verrà creata una bozza in Gmail con il PDF allegato. Potrai inviarla manualmente.</p>
          <div className="space-y-3 mt-2">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Destinatario</Label>
              <Input data-testid="gmail-to" value={gmailForm.to_email} onChange={(e) => setGmailForm({ ...gmailForm, to_email: e.target.value })} className="rounded-sm" placeholder="email@esempio.it" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Oggetto (opzionale)</Label>
              <Input data-testid="gmail-subject" value={gmailForm.subject} onChange={(e) => setGmailForm({ ...gmailForm, subject: e.target.value })} className="rounded-sm" placeholder={`${docLabel} ${docNumber || ""}`} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Messaggio (opzionale)</Label>
              <textarea data-testid="gmail-body" value={gmailForm.body} onChange={(e) => setGmailForm({ ...gmailForm, body: e.target.value })} className="input-field min-h-[80px] resize-y text-sm" rows={3} placeholder="In allegato il documento..." />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setGmailOpen(false)} className="rounded-sm">Annulla</Button>
              <Button onClick={handleCreateDraft} disabled={sendingDraft || !gmailForm.to_email}
                className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="create-draft-btn">
                {sendingDraft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                Crea Bozza
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
